import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { useConnectionState, useIsSpeaking } from "@livekit/components-react"
import { ConnectionState } from "livekit-client"
import * as PropTypes from "prop-types"
import React from "react"
import { View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FontAwesome6 from "react-native-vector-icons/FontAwesome6"

// The voice bars of a tile: they wave while the participant speaks, from what LiveKit already broadcasts
// (`isSpeaking`), on the web as on mobile. No audio analyser per tile: `useAudioWaveform` opened one AudioContext per
// tile on the web, for every student of the room (decision of 03/11).
const AudioWaveform = ({ trackRef, size }) => {
    const participant = trackRef?.participant
    const isMicEnabled = participant?.isMicrophoneEnabled

    // Subscribed here: the parent web tile does not re-render when the participant starts or stops speaking.
    // Room cut: no voice. The SDK does not reset « speaking » on a disconnection, and the bars would wave through
    // the whole reconnection (the same rule as `engine/live/room/speakingSource`).
    const connected = useConnectionState() === ConnectionState.Connected
    const isSpeaking = useIsSpeaking(participant) && connected

    const [animationPhase, setAnimationPhase] = React.useState(0)

    // Only animate when speaking and mic enabled
    React.useEffect(() => {
        if (isMicEnabled && isSpeaking) {
            const interval = setInterval(() => {
                setAnimationPhase((prev) => (prev + 1) % 12) // 12 phases for a smoother animation
            }, 150) // Update every 150ms for a subtle animation

            return () => clearInterval(interval)
        }
        return undefined
    }, [isMicEnabled, isSpeaking])

    const barCount = 3
    const baseHeights = [0.5, 0.5, 0.5] // Base pattern

    const getBarHeights = () => {
        // If mic is muted, return flat bars
        if (!isMicEnabled) {
            return [0.2, 0.2, 0.2].map((h) => h * size)
        }

        // If not speaking, return static bars
        if (!isSpeaking) {
            return baseHeights.map((h) => h * 0.4 * size) // Static but with microphone-on appearance
        }

        // Sine-like pattern, with a phase offset per bar: a wave flowing between the bars
        return baseHeights.map((baseHeight, index) => {
            const phase = (animationPhase + index * 2) % 12
            const amplitude = 0.15 // How much the bars move
            const variation = Math.sin((phase * Math.PI) / 6) * amplitude
            return (baseHeight + variation) * size
        })
    }

    // Get the heights for current state
    const barHeights = getBarHeights()

    return (
        <View
            style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                width: size * 1.2,
                height: size,
            }}
        >
            {[...Array(barCount)].map((_, index) => {
                return (
                    <View
                        key={index}
                        style={{
                            width: 3,
                            height: barHeights[index],
                            backgroundColor: theme.colors.success,
                            marginHorizontal: 1,
                            borderRadius: 10,
                        }}
                    />
                )
            })}
        </View>
    )
}

export function UsernameComponent(props) {
    return (
        <View
            style={{
                position: "relative",
                maxWidth: "90%",
                ...props.style,
            }}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    alignSelf: "center",
                    backgroundColor: !props.soundMode && props.noDisplayName ? (theme.colors.purple + "dd") : theme.colors.blackTransparent,
                    paddingHorizontal: props.noDisplayName ? props.condition ? 2 : 6 : 4,
                    paddingVertical: 2,
                    borderRadius: 6,
                    borderWidth: 2,
                    borderColor: theme.colors.transparent,
                    ...props.speakerStyles,
                }}
            >
                {!props.noDisplayName && (
                    <TextM
                        color={theme.colors.white}
                        fontSize={10}
                        style={{
                            textAlign: "center",
                            marginRight: 5,
                            marginLeft: 2,
                        }}
                    >
                        {props.userObject?.display_name ??
                            props.rtcProps?.username}{" "}
                        <TextM color={theme.colors.white} fontSize={9}>
                            {props.isOffline && "| hors-ligne"}
                        </TextM>
                    </TextM>
                )}
                {!props.isOffline &&
                    (props.isScreenShare ? (
                        <FontAwesome6
                            name={"display"}
                            size={12}
                            color={theme.colors.white}
                        />
                    ) : (
                        <>
                            {props.condition ? (
                                <FontAwesome5
                                    name="microphone-slash"
                                    size={12}
                                    color={
                                        theme.colors.white
                                    }
                                />
                            ) : (
                                <AudioWaveform
                                    trackRef={props.trackRef}
                                    size={12}
                                />
                            )}
                        </>
                    ))}
            </View>
        </View>
    )
}

UsernameComponent.propTypes = {
    userObject: PropTypes.any,
    rtcProps: PropTypes.any,
    user: PropTypes.any,
    condition: PropTypes.any,
    speakerStyles: PropTypes.any,
    isScreenShare: PropTypes.bool,
    noDisplayName: PropTypes.bool,
    style: PropTypes.any,
    soundMode: PropTypes.any,
    miniMode: PropTypes.any,
    trackRef: PropTypes.object, // LiveKit track reference
}
